import { memo } from "react";
import { Package, ZoomIn } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { money } from "@/lib/format";
import { imgSrc, IMG_SIZES } from "@/lib/img";

// Memoised so cart updates (which re-render the POS page) do not re-render every product card.
export const ProductCard = memo(function ProductCard({ p, imgSize, counter, onAdd, onFullPic }) {
  const nonStock = p.track_inventory === false;
  const out = !nonStock && p.quantity <= 0;
  const src = imgSrc(p);
  return (
    <button disabled={out} onClick={() => onAdd(p)} data-testid={`pos-product-${p.id}`} className="text-left disabled:opacity-40">
      <Card className="p-4 h-full hover:border-emerald-500/50 hover:shadow-lg transition-all active:scale-95">
        {src
          ? <div className={`relative w-full ${IMG_SIZES[imgSize].card} rounded-lg overflow-hidden bg-secondary mb-2`}>
              <img src={src} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              <span role="button" tabIndex={-1} aria-label="View full picture" data-testid={`pos-full-pic-${p.id}`}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onFullPic({ src, name: p.name + (p.variant ? ` · ${p.variant}` : "") }); }}
                className="absolute bottom-1 right-1 rounded-md bg-background/80 backdrop-blur p-1 text-foreground/80 hover:text-foreground"><ZoomIn className="w-4 h-4" /></span>
            </div>
          : <div className={`w-full ${IMG_SIZES[imgSize].card} rounded-lg bg-secondary/60 mb-2 flex items-center justify-center`}><Package className="w-8 h-8 text-muted-foreground/40" /></div>}
        <div className="flex justify-between items-start gap-1">
          <p className={`${counter ? "font-semibold text-base leading-snug line-clamp-3" : "font-semibold text-[15px] leading-snug line-clamp-3"} whitespace-normal break-words`}>{p.name}</p>
          {!nonStock && p.stock_status !== "IN STOCK" && (
            <Badge className={`text-[9px] shrink-0 ${out ? "bg-rose-500/15 text-rose-500 border-rose-500/30" : "bg-amber-500/15 text-amber-500 border-amber-500/30"}`}>
              {out ? "OUT" : "LOW"}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{p.brand ? <span>{p.brand} · </span> : ""}{p.variant ? <span className="font-semibold">{p.variant} · </span> : ""}{p.model ? <span>Model {p.model} · </span> : ""}{p.unit ? <span className="capitalize">{p.unit} · </span> : ""}{p.category}</p>
        {p.sku && <p className="text-[10px] mono text-slate-500 dark:text-slate-400 truncate">SKU: {p.sku}</p>}
        <div className="flex items-end justify-between mt-3">
          <span className={`${counter ? "mono font-bold text-lg" : "mono font-bold"} text-emerald-600 dark:text-emerald-400`}>{money(p.price)}</span>
          <span className="text-[10px] text-slate-500 dark:text-slate-400 mono">{nonStock ? "Non-Stock · to order" : `${p.quantity} ${p.unit} avail.`}</span>
        </div>
      </Card>
    </button>
  );
});
